import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useMenuItemsContext } from "@/context/MenuItemsContext";
import Image from "next/image";
import Link from "next/link";
import {
  ArrowUpCircleIcon,
  ChevronDownIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { HiBuildingOffice2 } from "react-icons/hi2";
import { Button } from "@/components/ui/Button";
import { useState } from "react";
import { getEstateLimit } from "@/utils/membershipLimits";
import {
  HintWindow,
  HintWindowBody,
  HintWindowHeader,
} from "@/components/ui/HintWindow";
import { cn } from "@/lib/utils";

const Profile = ({ compact = false }: { compact?: boolean }) => {
  const { errorMessage, isDark, userInfoGlobal } = useAppContext();
  const { language } = useLanguage();
  const { setIsOpen, setSelectedItem } = useMenuItemsContext();
  const [isCompactOverviewOpen, setIsCompactOverviewOpen] = useState(false);
  const toggleCompactOverview = () =>
    setIsCompactOverviewOpen((current) => !current);
  const isBasicClient = userInfoGlobal?.membership === "BASIC";
  const estateLimit = getEstateLimit(userInfoGlobal?.membership);
  const copy = {
    cs: {
      membership: "Členství",
      promoCode: "Promo kód",
      discount: "sleva",
      forever: "Napořád",
      free: "Zdarma",
      price: "Cena předplatného",
      nextPeriod: "Další období",
      userName: "Jméno",
      billingSubject: "Odběratel",
      address: "Adresa",
      idNumber: "IČ",
      validTo: "Platné do",
      overview: "Přehled účtu",
      upgrade: "Zvýšit členství",
      watchdog: "Hlídací pes",
      portfolioAllowance:
        estateLimit == null
          ? null
          : `až ${estateLimit} ${estateLimit === 1 ? "nemovitost" : estateLimit < 5 ? "nemovitosti" : "nemovitostí"} v portfoliu`,
      accountDetails: "Členství a fakturační údaje",
      showAccountDetails: "Zobrazit přehled účtu",
      hideAccountDetails: "Skrýt přehled účtu",
    },
    en: {
      membership: "Membership",
      promoCode: "Promo code",
      discount: "discount",
      forever: "Forever",
      free: "Free",
      price: "Subscription price",
      nextPeriod: "Next period",
      userName: "Name",
      billingSubject: "Billing subject",
      address: "Address",
      idNumber: "Company ID",
      validTo: "Valid until",
      overview: "Account overview",
      upgrade: "Upgrade now",
      watchdog: "WatchDog",
      portfolioAllowance:
        estateLimit == null
          ? null
          : `up to ${estateLimit} ${estateLimit === 1 ? "property" : "properties"} in the portfolio`,
      accountDetails: "Membership and billing details",
      showAccountDetails: "Show account overview",
      hideAccountDetails: "Hide account overview",
    },
  }[language];

  const openUpgradeModal = () => {
    setSelectedItem({
      name: "Změnit členství",
      href: "",
      icon: ArrowUpCircleIcon,
    });
    setIsOpen(true);
  };

  const promoDescription = userInfoGlobal?.promoCode
    ? [
        userInfoGlobal.promoCode,
        userInfoGlobal.promoDiscountPercent == null
          ? null
          : `${userInfoGlobal.promoDiscountPercent}% ${copy.discount}`,
        userInfoGlobal.promoLifetime ? copy.forever : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : null;

  const rows = [
    { label: "Email", value: userInfoGlobal?.email },
    ...(userInfoGlobal?.role === "ADMIN"
      ? [{ label: "Role", value: userInfoGlobal?.role }]
      : []),
    {
      label: copy.membership,
      value: [
        userInfoGlobal?.membership,
        userInfoGlobal?.membershipInfo,
        copy.portfolioAllowance,
      ]
        .filter(Boolean)
        .join(" · "),
    },
    ...(userInfoGlobal?.promoCode
      ? [
          {
            label: copy.promoCode,
            value: promoDescription,
          },
        ]
      : []),
    ...(userInfoGlobal?.membership !== "BASIC"
      ? [
          {
            label: copy.price,
            value: userInfoGlobal?.promoFree
              ? copy.free
              : `${userInfoGlobal?.agreedPrice}.00 ${language === "cs" ? "Kč" : "CZK"}`,
          },
          {
            label: copy.nextPeriod,
            value: userInfoGlobal?.promoLifetime
              ? copy.forever
              : userInfoGlobal?.validToFormatted,
          },
          { label: copy.userName, value: userInfoGlobal?.userName },
          { label: copy.billingSubject, value: userInfoGlobal?.subject },
          { label: copy.address, value: userInfoGlobal?.address },
          ...(userInfoGlobal?.idNumber
            ? [{ label: copy.idNumber, value: userInfoGlobal?.idNumber }]
            : []),
        ]
      : [{ label: copy.validTo, value: userInfoGlobal?.validToFormatted }]),
  ];

  if (compact) {
    const compactRows = rows.filter((row) => Boolean(row.value));

    return (
      <HintWindow className="w-full">
        <HintWindowHeader
          className={cn(
            "group w-full text-left transition-colors hover:bg-softiq-surface-cool dark:hover:bg-white/[0.04]",
            !isCompactOverviewOpen && "rounded-b-xl border-b",
          )}
        >
          <button
            type="button"
            aria-expanded={isCompactOverviewOpen}
            aria-controls="compact-account-overview"
            aria-label={
              isCompactOverviewOpen
                ? copy.hideAccountDetails
                : copy.showAccountDetails
            }
            onClick={toggleCompactOverview}
            className="flex min-w-0 flex-1 items-center gap-3 text-left"
          >
            <span className="app-hint-window__icon">
              <InformationCircleIcon aria-hidden="true" className="size-5" />
            </span>
            <span className="app-hint-window__copy">
              <span className="block text-[10px] font-medium uppercase tracking-[0.18em] text-softiq-primary/50 dark:text-white/50">
                {copy.overview}
              </span>
              <span
                data-tour="dashboard-profile-billing"
                className="block truncate text-sm font-medium text-softiq-primary dark:text-white"
              >
                {copy.accountDetails}
              </span>
            </span>
          </button>
          {isBasicClient ? (
            <Button variant="default" size="compact" onClick={openUpgradeModal}>
              <ArrowUpCircleIcon className="size-4 flex-none" />
              {copy.upgrade}
            </Button>
          ) : null}
          <button
            type="button"
            aria-expanded={isCompactOverviewOpen}
            aria-controls="compact-account-overview"
            aria-label={
              isCompactOverviewOpen
                ? copy.hideAccountDetails
                : copy.showAccountDetails
            }
            onClick={toggleCompactOverview}
            className="flex size-9 shrink-0 items-center justify-center rounded-full border border-transparent bg-background text-foreground shadow-sm transition-colors hover:border-input focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-softiq-secondary/40"
          >
            <ChevronDownIcon
              aria-hidden="true"
              className={`size-5 transition-transform duration-200 ${
                isCompactOverviewOpen ? "rotate-180" : ""
              }`}
            />
          </button>
        </HintWindowHeader>

        <div
          id="compact-account-overview"
          className={`grid transition-[grid-template-rows] duration-200 ease-out ${
            isCompactOverviewOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          }`}
        >
          <div className="overflow-hidden">
            <HintWindowBody
              className={cn(
                !isCompactOverviewOpen && "rounded-none border-0 p-0",
              )}
            >
              <div className="grid gap-x-10 gap-y-5 px-5 py-5 sm:grid-cols-2 sm:px-6 xl:grid-cols-4">
                {compactRows.map((row) => (
                  <div key={row.label} className="min-w-0">
                    <p className="app-section-label">{row.label}</p>
                    <p className="mt-1 truncate text-xs font-medium">
                      {row.value}
                    </p>
                  </div>
                ))}
              </div>

              {errorMessage ? (
                <div className="border-t border-red-200 bg-red-50 px-5 py-3 text-xs text-red-600 dark:border-red-400/20 dark:bg-red-400/10 dark:text-red-300 sm:px-6">
                  {errorMessage}
                </div>
              ) : null}
            </HintWindowBody>
          </div>
        </div>
      </HintWindow>
    );
  }

  return (
    <div className="herocard-root">
      <div className="herocard-body">
        <p className="herocard-eyebrow">{copy.overview}</p>

        {rows.map(
          (row, i) =>
            row.value && (
              <div key={i} className="herocard-row">
                <span className="herocard-row-label">{row.label}</span>
                <span className="herocard-row-value">{row.value}</span>
              </div>
            ),
        )}

        {errorMessage && (
          <div className="herocard-error">
            <p className="herocard-error-text">{errorMessage}</p>
          </div>
        )}

        {isBasicClient && (
          <div className="flex justify-center align-center my-auto">
            <Button variant="default" size="lg" onClick={openUpgradeModal}>
              <ArrowUpCircleIcon className="size-6 flex-none" />
              {copy.upgrade}
            </Button>
          </div>
        )}
      </div>

      <div className="herocard-nav">
        <Button asChild variant="outline" className="w-full">
          <Link href="/portfolio">
            <HiBuildingOffice2 className="size-4 flex-none" />
            Portfolio
          </Link>
        </Button>
        <Button asChild variant="outline" className="w-full">
          <Link href="/hlidaci-pes">
            <Image
              height={16}
              width={16}
              alt="paw"
              src={
                isDark
                  ? "/image/paw/paw-solid.svg"
                  : "/image/paw/paw-solid-dark.svg"
              }
              className="h-4 w-auto"
            />
            {copy.watchdog}
          </Link>
        </Button>
      </div>
    </div>
  );
};

export default Profile;
